import Feature from 'ol/Feature'
import GeoJSON from 'ol/format/GeoJSON'
import Map from 'ol/Map'
import View from 'ol/View'
import {
  Circle as CircleStyle,
  Fill,
  Icon,
  Stroke,
  Style,
  Text,
} from 'ol/style'
import { Cluster, Vector as VectorSource, XYZ } from 'ol/source'
import { LineString, Point, Polygon } from 'ol/geom'
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import { createEmpty, extend, getWidth } from 'ol/extent'
import { fromLonLat } from 'ol/proj'
import { monotoneChainConvexHull } from './monotone-chain-convex-hull'
import Select from 'ol/interaction/Select'
import SelectMulti from 'ol-ext/control/SelectMulti'
import SelectFulltext from 'ol-ext/control/SelectFulltext'
import SelectPopup from 'ol-ext/control/SelectPopup'
import SelectCheck from 'ol-ext/control/SelectCheck'
import SelectCondition from 'ol-ext/control/SelectCondition'

import { pointData } from './geoJson'
import './style.css'
import { singleClick } from 'ol/events/condition'

let mygeojson = {
  type: 'FeatureCollection',
  crs: {
    type: 'name',
    properties: {
      name: 'EPSG:3857',
    },
  },
  features: [],
}
for (let point of pointData) {
  let coordinate = fromLonLat([
    parseFloat(point.longitude),
    parseFloat(point.latitude),
  ])
  let properties = point
  delete properties.longitude
  delete properties.latitude
  let feature = {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: coordinate },
    properties: properties,
  }
  mygeojson.features.push(feature)
}

const circleDistanceMultiplier = 1
const circleFootSeparation = 28
const circleStartAngle = Math.PI / 2

const convexHullFill = new Fill({
  color: 'rgba(255, 153, 0, 0.4)',
})
const convexHullStroke = new Stroke({
  color: 'rgba(204, 85, 0, 1)',
  width: 1.5,
})
const outerCircleFill = new Fill({
  color: 'rgba(255, 153, 102, 0.3)',
})
const innerCircleFill = new Fill({
  color: 'rgba(255, 165, 0, 0.7)',
})

const fill = new Fill({
  color: 'rgba(255,25,25,1)',
})
const stroke = new Stroke({
  color: '#3399CC',
  width: 1.25,
})

/**
 * Elements that make up the table body.
 */
const content = document.getElementById('table-body')

/**
 * Single feature style, users for clusters with 1 feature and cluster circles.
 * @param {Feature} clusterMember A feature from a cluster.
 * @return {Style} An icon style for the cluster member's location.
 */
function clusterMemberStyle(clusterMember) {
  console.log(clusterMember.get('jobstatus'))
  return new Style({
    geometry: clusterMember.getGeometry(),
    image: new CircleStyle({
      fill: fill,
      stroke: stroke,
      radius: 5,
    }),
    // image: clusterMember.get('LEISTUNG') > 5 ? darkIcon : lightIcon,
  })
}

let clickFeature, clickResolution
/**
 * Style for clusters with features that are too close to each other, activated on click.
 * @param {Feature} cluster A cluster with overlapping members.
 * @param {number} resolution The current view resolution.
 * @return {Style} A style to render an expanded view of the cluster members.
 */
function clusterCircleStyle(cluster, resolution) {
  if (cluster !== clickFeature || resolution !== clickResolution) {
    return
  }
  const clusterMembers = cluster.get('features')
  const centerCoordinates = cluster.getGeometry().getCoordinates()
  return generatePointsCircle(
    clusterMembers.length,
    cluster.getGeometry().getCoordinates(),
    resolution
  ).reduce((styles, coordinates, i) => {
    const point = new Point(coordinates)
    const line = new LineString([centerCoordinates, coordinates])
    styles.unshift(
      new Style({
        geometry: line,
        stroke: convexHullStroke,
      })
    )
    styles.push(
      clusterMemberStyle(
        new Feature({
          ...clusterMembers[i].getProperties(),
          geometry: point,
        })
      )
    )
    return styles
  }, [])
}

/**
 * From
 * https://github.com/Leaflet/Leaflet.markercluster/blob/31360f2/src/MarkerCluster.Spiderfier.js#L55-L72
 * Arranges points in a circle around the cluster center, with a line pointing from the center to
 * each point.
 * @param {number} count Number of cluster members.
 * @param {Array<number>} clusterCenter Center coordinate of the cluster.
 * @param {number} resolution Current view resolution.
 * @return {Array<Array<number>>} An array of coordinates representing the cluster members.
 */
function generatePointsCircle(count, clusterCenter, resolution) {
  const circumference =
    circleDistanceMultiplier * circleFootSeparation * (2 + count)
  let legLength = circumference / (Math.PI * 2) //radius from circumference
  const angleStep = (Math.PI * 2) / count
  const res = []
  let angle

  legLength = Math.max(legLength, 35) * resolution // Minimum distance to get outside the cluster icon.

  for (let i = 0; i < count; ++i) {
    // Clockwise, like spiral.
    angle = circleStartAngle + i * angleStep
    res.push([
      clusterCenter[0] + legLength * Math.cos(angle),
      clusterCenter[1] + legLength * Math.sin(angle),
    ])
  }

  return res
}

let hoverFeature
/**
 * Style for convex hulls of clusters, activated on hover.
 * @param {Feature} cluster The cluster feature.
 * @return {Style} Polygon style for the convex hull of the cluster.
 */
function clusterHullStyle(cluster) {
  if (cluster !== hoverFeature) {
    return
  }
  const originalFeatures = cluster.get('features')
  const points = originalFeatures.map((feature) =>
    feature.getGeometry().getCoordinates()
  )
  return new Style({
    geometry: new Polygon([monotoneChainConvexHull(points)]),
    fill: convexHullFill,
    stroke: convexHullStroke,
  })
}

const styleCache = {}
function clusterStyle(feature) {
  const size = feature.get('features').length
  let style = styleCache[size]
  if (!style) {
    let color =
      size > 100
        ? '255, 0, 0'
        : size > 25
        ? '248, 128, 0'
        : size > 8
        ? '248, 192, 0'
        : '128, 192, 64'
    let radius = Math.max(8, Math.min(size * 0.75, 20))
    style = styleCache[size] = [
      new Style({
        image: new CircleStyle({
          radius: radius + 2,
          stroke: new Stroke({ color: 'rgba(' + color + ',0.3)', width: 4 }),
        }),
      }),
      new Style({
        image: new CircleStyle({
          radius: radius,
          fill: new Fill({ color: 'rgba(' + color + ',0.6)' }),
        }),
        text: new Text({
          text: size.toString(),
          fill: new Fill({ color: '#000' }),
        }),
      }),
    ]
  }
  return style
}

const vectorSource = new VectorSource({
  features: new GeoJSON().readFeatures(mygeojson),
})

const clusterSource = new Cluster({
  distance: 40,
  source: vectorSource,
})

// Layer displaying the convex hull of the hovered cluster.
const clusterHulls = new VectorLayer({
  source: clusterSource,
  style: clusterHullStyle,
})

// Layer displaying the clusters and individual features.
const clusters = new VectorLayer({
  source: clusterSource,
  style: clusterStyle,
})

// Layer displaying the expanded view of overlapping cluster members.
const clusterCircles = new VectorLayer({
  source: clusterSource,
  style: clusterCircleStyle,
})

const raster = new TileLayer({
  source: new XYZ({
    url:
      'https://{a-c}.tile.thunderforest.com/cycle/{z}/{x}/{y}.png' +
      '?apikey=0e6fc415256d4fbb9b5166a718591d71',
  }),
})
;('https://c.tile.thunderforest.com/cycle/12/2001/1277.png?apikey=0e6fc415256d4fbb9b5166a718591d71')

const map = new Map({
  target: 'map',
  layers: [raster, clusterHulls, clusters, clusterCircles],
  view: new View({
    center: fromLonLat([-1.9075221436896852, 54.06845934060873]),
    zoom: 6,
    maxZoom: 19,
    // extent: [
    //   ...fromLonLat([-1.9075221436896852, 54.06845934060873]),
    //   ...fromLonLat([-1.9075221436896852, 54.06845934060873]),
    // ],
    showFullExtent: true,
  }),
})

map.on('pointermove', (event) => {
  clusters.getFeatures(event.pixel).then((features) => {
    if (features[0] !== hoverFeature) {
      // Display the convex hull on hover.
      hoverFeature = features[0]
      clusterHulls.setStyle(clusterHullStyle)
      // Change the cursor style to indicate that the cluster is clickable.
      map.getTargetElement().style.cursor =
        hoverFeature && hoverFeature.get('features').length > 1 ? 'pointer' : ''
    }
  })
})

map.on('click', (event) => {
  clusters.getFeatures(event.pixel).then((features) => {
    if (features.length > 0) {
      const clusterMembers = features[0].get('features')
      if (clusterMembers.length > 1) {
        // Calculate the extent of the cluster members.
        const extent = createEmpty()
        clusterMembers.forEach((feature) =>
          extend(extent, feature.getGeometry().getExtent())
        )
        const view = map.getView()
        const resolution = map.getView().getResolution()
        if (
          view.getZoom() === view.getMaxZoom() ||
          (getWidth(extent) < resolution && getWidth(extent) < resolution)
        ) {
          // Show an expanded view of the cluster members.
          clickFeature = features[0]
          clickResolution = resolution
          clusterCircles.setStyle(clusterCircleStyle)
        } else {
          // Zoom to the extent of the cluster members.
          view.fit(extent, { duration: 500, padding: [50, 50, 50, 50] })
        }
      }
    }
  })
})

map.on('pointermove', (event) => {
  let features = []
  map.forEachFeatureAtPixel(
    event.pixel,
    (feature, layer) => {
      features = feature.get('features')
      const valuesToShow = []
      if (features && features.length > 0) {
        features.forEach((clusterFeature) => {
          valuesToShow.push(
            `<tr>
              <td>${clusterFeature.get('jobnumber')}</td>
              <td>${clusterFeature.get('jobstatus')}</td>
              <td>${clusterFeature.get('sitename')}</td>
              <td>${clusterFeature.get('postcode')}</td>
              <td>${clusterFeature.get('workorder')}</td>
              <td>${clusterFeature.get('description')}</td>
              <td>${clusterFeature.get('category')}</td>
              <td>${clusterFeature.get('responsedue')}</td>
              <td>'datamissing'</td>
            </tr>`
          )
        })
        content.innerHTML = valuesToShow.join('')
        content.hidden = false
      }
    },
    {
      layerFilter: (layer) => {
        return layer.type === new VectorLayer().type ? true : false
      },
      hitTolerance: 6,
    }
  )
  if (!features || features.length === 0) {
  }
})

map.on('singleclick', (event) => {
  let features = []
  map.forEachFeatureAtPixel(
    event.pixel,
    (feature, layer) => {
      console.log(feature)
      features = feature.get('features')
      const valuesToShow = []
      if (features && features.length > 0) {
        features.forEach((clusterFeature) => {
          valuesToShow.push(
            `<tr>
              <td>${clusterFeature.get('jobnumber')}</td>
              <td>${clusterFeature.get('jobstatus')}</td>
              <td>${clusterFeature.get('sitename')}</td>
              <td>${clusterFeature.get('postcode')}</td>
              <td>${clusterFeature.get('workorder')}</td>
              <td>${clusterFeature.get('description')}</td>
              <td>${clusterFeature.get('category')}</td>
              <td>${clusterFeature.get('responsedue')}</td>
              <td>'datamissing'</td>
            </tr>`
          )
        })
        content.innerHTML = valuesToShow.join('')
        content.hidden = false
        overlay.setPosition(event.coordinate)
      }
    },
    {
      layerFilter: (layer) => {
        return layer.type === new VectorLayer().type ? true : false
      },
      hitTolerance: 6,
    }
  )
  if (!features || features.length === 0) {
    content.innerHTML = ''
  }
})

// // Select  interaction
// const selecti = new Select({
//   hitTolerance: 5,
//   condition: singleClick,
// })
// map.addInteraction(selecti)
// // Select feature when click on the reference index
// selecti.on('select', function (e) {
//   var f = e.selected[0]
//   if (f) {
//     var prop = f.getProperties()
//     var ul = $('.options ul').html('')
//     for (var p in prop)
//       if (p !== 'geometry') {
//         if (p === 'img')
//           $('<li>').appendTo(ul).append($('<img>').attr('src', prop[p]))
//         else
//           $('<li>')
//             .text(p + ': ' + prop[p])
//             .appendTo(ul)
//       }
//   }
// })

// // Select control
// var selectCtrl = new SelectMulti({
//   //target: $('.options').get(0),
//   controls: [
//     new SelectCheck({
//       label: 'Author:',
//       property: 'author',
//       // type: 'radio',
//       values: {
//         'Opérateur D ; Brissy, Edouard': 'Brissy, Edouard',
//         'Opérateur D ; Sélince': 'Sélince',
//         'Opérateur Théta (code armée, photographe)': 'Théta',
//       },
//       sort: true,
//     }),
//     new SelectCondition({
//       label: 'before 1918',
//       condition: { attr: 'date', op: '<', val: '1918' },
//     }),
//   ],
// })
// // Add control
// map.addControl(selectCtrl)

// // Do something on select
// selectCtrl.on('select', function (e) {
//   selecti.getFeatures().clear()
//   console.log(e)
//   if ('#select'.prop('checked')) {
//     // Select features
//     e.features.forEach(function (f) {
//       selecti.getFeatures().push(f)
//     })
//   } else {
//     // Hide features
//     vectorSource.getFeatures().forEach(function (f) {
//       f.setStyle([])
//     })
//     // Show current
//     e.features.forEach(function (f) {
//       f.setStyle(null)
//     })
//   }
// })
// // Show all features
// function reset() {
//   selecti.getFeatures().clear()
//   vectorSource.getFeatures().forEach(function (f) {
//     f.setStyle(null)
//   })
//   selectCtrl.doSelect()
// }
// // Set values when loaded
// var listenerKey = vectorSource.on('change', function (e) {
//   if (vectorSource.getState() === 'ready') {
//     ol.Observable.unByKey(listenerKey)
//     // Fill the popup with the features values
//     selectCtrl.getControls()[1].setValues({ sort: true })
//   }
// })

// onClick('gender', function () {
//   // clear filteredGeoJSON
//   filteredGeoJSON = {
//     type: 'FeatureCollection',
//     features: [],
//   }
//   filteredSource = new VectorSource({
//     features: filterGeoJSON('protestantisch', 'f'),
//   })
//   filteredLayer.setSource(filteredSource)
// })
